import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Search, SlidersHorizontal, X, ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, Upload, Save, Settings, GripVertical, Pencil, DollarSign, Eye, EyeOff, ImageIcon, Trash2, Plus, Ruler, Palette, Home } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import ProductCard from "@/components/ProductCard";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCTS_PER_PAGE = 40;

// Helper function to remove "Mini" from product names
const removeMinifromName = (name) => {
  if (!name) return '';
  return name.replace(/\bMini\b\s*/gi, '').trim();
};

export const Products = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, token } = useAuth();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "1"));
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState(category || "");
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "default");
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [itemsPerPage, setItemsPerPage] = useState(PRODUCTS_PER_PAGE);
  const [selectedMiniModel, setSelectedMiniModel] = useState("");
  const [selectedColour, setSelectedColour] = useState("");
  
  // Mini Related section state
  const [miniRelatedUrl, setMiniRelatedUrl] = useState("");
  const [miniRelatedImage, setMiniRelatedImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [miniRelatedLinkUrl, setMiniRelatedLinkUrl] = useState("");
  const [miniRelatedText, setMiniRelatedText] = useState("");
  const [savingAdvert, setSavingAdvert] = useState(false);
  const [allAdverts, setAllAdverts] = useState([]);
  
  // Admin panel state
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [adminEditMode, setAdminEditMode] = useState(null); // 'reorder', 'price', 'edit'
  const [editingProduct, setEditingProduct] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [priceEdits, setPriceEdits] = useState({});
  const [reorderList, setReorderList] = useState([]);
  const [savingChanges, setSavingChanges] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [editingMaterialPrices, setEditingMaterialPrices] = useState(false);
  const [materialPriceEdits, setMaterialPriceEdits] = useState({});
  
  // Category editing state (admin)
  const [categoryEditMode, setCategoryEditMode] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryEditModal, setCategoryEditModal] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', image_url: '' });
  const [uploadingCategoryImage, setUploadingCategoryImage] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [categoryOrder, setCategoryOrder] = useState([]);
  
  // Product reordering state (admin)
  const [productEditMode, setProductEditMode] = useState(false);
  const [savingProductOrder, setSavingProductOrder] = useState(false);
  
  // Category/Page settings state
  const [pageSettingsOpen, setPageSettingsOpen] = useState(false);
  const [categorySettings, setCategorySettings] = useState({
    name: '',
    slug: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    displayOrder: 'default'
  });
  const [savingCategorySettings, setSavingCategorySettings] = useState(false);

  // Load all active adverts on mount
  useEffect(() => {
    const fetchAdverts = async () => {
      try {
        const res = await axios.get(`${API}/adverts/active`);
        if (res.data && res.data.length > 0) {
          setAllAdverts(res.data);
          // Set the first sidebar advert for editing
          const sidebarAdvert = res.data.find(a => a.position === 'sidebar');
          if (sidebarAdvert) {
            setMiniRelatedUrl(sidebarAdvert.image_url || "");
            setMiniRelatedLinkUrl(sidebarAdvert.link_url || "");
            setMiniRelatedText(sidebarAdvert.description || "");
            if (sidebarAdvert.image_url) {
              setMiniRelatedImage(sidebarAdvert.image_url);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching adverts:", error);
      }
    };
    fetchAdverts();
  }, []);

  // Save sidebar advert
  const saveSidebarAdvert = async () => {
    if (!token) return;
    setSavingAdvert(true);
    try {
      await axios.put(`${API}/adverts/sidebar/quick`, {
        image_url: miniRelatedImage || miniRelatedUrl,
        link_url: miniRelatedLinkUrl,
        description: miniRelatedText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Advert saved successfully!");
    } catch (error) {
      console.error("Error saving advert:", error);
      alert("Failed to save advert");
    } finally {
      setSavingAdvert(false);
    }
  };

  // Category editing functions
  const openCategoryEditModal = (cat) => {
    setEditingCategory(cat);
    setCategoryFormData({ name: cat.name, image_url: cat.image_url || '' });
    setCategoryEditModal(true);
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingCategoryImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      setCategoryFormData(prev => ({ ...prev, image_url: res.data.url }));
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image");
    } finally {
      setUploadingCategoryImage(false);
    }
  };

  const saveCategory = async () => {
    if (!editingCategory || !token) return;
    
    setSavingCategory(true);
    try {
      await axios.put(`${API}/categories/${editingCategory.id}`, {
        name: categoryFormData.name,
        image_url: categoryFormData.image_url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setCategories(prev => prev.map(cat => 
        cat.id === editingCategory.id 
          ? { ...cat, name: categoryFormData.name, image_url: categoryFormData.image_url }
          : cat
      ));
      setCategoryEditModal(false);
      setEditingCategory(null);
    } catch (error) {
      console.error("Error saving category:", error);
      alert("Failed to save category");
    } finally {
      setSavingCategory(false);
    }
  };

  const moveCategoryOrder = async (catId, direction) => {
    if (!token) {
      alert("Please login as admin to reorder categories");
      return;
    }
    
    // Get subcategories of mini-stripes for ordering
    const miniStripesRoot = categories.find(c => c.slug === 'mini-stripes');
    if (!miniStripesRoot) {
      console.error("Mini stripes root category not found");
      return;
    }
    
    const subcats = categories
      .filter(c => c.parent_id === miniStripesRoot.id && c.name !== 'Mini 60 years')
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || a.name.localeCompare(b.name));
    
    const currentIndex = subcats.findIndex(c => c.id === catId);
    if (currentIndex === -1) {
      console.error("Category not found in subcategories");
      return;
    }
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= subcats.length) {
      console.log("Already at boundary, cannot move further");
      return;
    }
    
    // Swap display orders
    const currentCat = subcats[currentIndex];
    const swapCat = subcats[newIndex];
    
    console.log(`Moving ${currentCat.name} ${direction}: swapping with ${swapCat.name}`);
    
    try {
      // Update current category
      await axios.put(`${API}/categories/${currentCat.id}`, {
        display_order: newIndex
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      // Update swap category
      await axios.put(`${API}/categories/${swapCat.id}`, {
        display_order: currentIndex
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      // Refresh categories
      const res = await axios.get(`${API}/categories/all`);
      setCategories(res.data);
      
      console.log("Category order updated successfully");
    } catch (error) {
      console.error("Error reordering categories:", error);
      alert("Failed to reorder categories. Please try again.");
    }
  };

  // Product reordering function
  const moveProductOrder = async (productId, direction) => {
    if (!token) {
      alert("Please login as admin to reorder products");
      return;
    }
    
    const currentIndex = products.findIndex(p => p.id === productId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= products.length) return;
    
    const currentProduct = products[currentIndex];
    const swapProduct = products[newIndex];
    
    setSavingProductOrder(true);
    
    try {
      // Update display_order for both products
      await axios.put(`${API}/products/${currentProduct.id}/order`, {
        display_order: newIndex
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      await axios.put(`${API}/products/${swapProduct.id}/order`, {
        display_order: currentIndex
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      // Swap locally for immediate feedback
      const newProducts = [...products];
      newProducts[currentIndex] = swapProduct;
      newProducts[newIndex] = currentProduct;
      setProducts(newProducts);
      
    } catch (error) {
      console.error("Error reordering products:", error);
      alert("Failed to reorder products. Please try again.");
    } finally {
      setSavingProductOrder(false);
    }
  };

  const [categoriesError, setCategoriesError] = useState(false);
  const [seedingDatabase, setSeedingDatabase] = useState(false);

  const triggerDatabaseSeed = async () => {
    setSeedingDatabase(true);
    try {
      const res = await axios.get(`${API}/seed-database`);
      console.log("Database seed result:", res.data);
      // Refresh categories after seeding
      const catRes = await axios.get(`${API}/categories/all`);
      setCategories(catRes.data || []);
      setCategoriesError(false);
    } catch (error) {
      console.error("Error seeding database:", error);
      alert("Failed to seed database. Please try again.");
    } finally {
      setSeedingDatabase(false);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        console.log("Fetching categories from:", `${API}/categories/all`);
        const res = await axios.get(`${API}/categories/all`);
        console.log("Categories received:", res.data?.length || 0);
        setCategories(res.data || []);
        setCategoriesError(false);
        
        // If no categories found, try to seed automatically
        if (!res.data || res.data.length === 0) {
          console.log("No categories found, attempting auto-seed...");
          try {
            await axios.get(`${API}/seed-database`);
            // Re-fetch after seeding
            const retryRes = await axios.get(`${API}/categories/all`);
            setCategories(retryRes.data || []);
          } catch (seedError) {
            console.error("Auto-seed failed:", seedError);
          }
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
        setCategoriesError(true);
      }
    };
    const fetchMaterials = async () => {
      try {
        const res = await axios.get(`${API}/materials`);
        setMaterials(res.data || []);
      } catch (error) {
        console.error("Error fetching materials:", error);
      }
    };
    fetchCategories();
    fetchMaterials();
  }, []);

  // Reset to page 1 when category or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [category, debouncedSearch]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category) params.set("category", category);
        if (debouncedSearch) params.set("search", debouncedSearch);
        params.set("limit", PRODUCTS_PER_PAGE);
        params.set("page", currentPage);
        
        const res = await axios.get(`${API}/products/paginated?${params.toString()}`);
        let sorted = [...res.data.products];
        
        if (sortBy === "price-asc") {
          sorted.sort((a, b) => a.price - b.price);
        } else if (sortBy === "price-desc") {
          sorted.sort((a, b) => b.price - a.price);
        } else if (sortBy === "name-asc") {
          sorted.sort((a, b) => a.name.localeCompare(b.name));
        } else if (sortBy === "name-desc") {
          sorted.sort((a, b) => b.name.localeCompare(a.name));
        } else if (sortBy === "newest") {
          sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        }
        
        setProducts(sorted);
        setTotalPages(res.data.total_pages);
        setTotalProducts(res.data.total);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, debouncedSearch, sortBy, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Update URL with page number
      const params = new URLSearchParams(searchParams);
      if (newPage > 1) {
        params.set("page", newPage.toString());
      } else {
        params.delete("page");
      }
      setSearchParams(params);
      // Scroll to top of products
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set("search", searchQuery);
    } else {
      params.delete("search");
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setSortBy("default");
    setCurrentPage(1);
    setSearchParams({});
  };

  const FilterSidebar = () => {
    // Clean pill-shaped category item
    const CategoryItem = ({ cat, isSubcategory = false }) => {
      const subcats = categories
        .filter(sub => sub.parent_id === cat.id)
        .sort((a, b) => a.name.localeCompare(b.name));
      const hasSubcats = subcats.length > 0;
      const isActive = category === cat.slug;
      const [isExpanded, setIsExpanded] = useState(isActive);
      const productCount = cat.product_count || 0;
      
      return (
        <div className="mb-5">
          {/* Main Category - Compact black pill */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                setSelectedCategory(cat.slug);
                setFilterOpen(false);
                window.location.href = `/products/${cat.slug}`;
              }}
              className={`text-left px-3 py-1.5 text-xs rounded-full transition-all duration-200 border-2 transform hover:scale-105 hover:shadow-lg flex items-center gap-1.5 ${
                isActive 
                  ? "bg-black text-white font-semibold border-black shadow-md" 
                  : "bg-black text-white font-medium border-black hover:bg-gray-700 hover:-translate-y-0.5"
              }`}
              data-testid={`filter-${cat.slug}`}
              title={cat.name}
            >
              <span>{cat.name}</span>
              {productCount > 0 && (
                <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] rounded-full bg-white font-medium text-black">
                  {productCount}
                </span>
              )}
            </button>
            {hasSubcats && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 rounded-full hover:bg-secondary-foreground/10 transition-colors"
              >
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
          
          {/* Subcategories */}
          {hasSubcats && isExpanded && (
            <div className="flex flex-wrap gap-1.5 mt-2 ml-3 pb-1">
              {subcats.map(sub => {
                const subCount = sub.product_count || 0;
                return (
                  <button
                    key={sub.id}
                    onClick={() => {
                      setSelectedCategory(sub.slug);
                      setFilterOpen(false);
                      window.location.href = `/products/${sub.slug}`;
                    }}
                    className={`text-left px-3 py-1 text-xs rounded-full transition-all duration-200 border transform hover:scale-105 hover:shadow-md flex items-center gap-1 ${
                      category === sub.slug 
                        ? "bg-black text-white font-medium border-black shadow-sm" 
                        : "bg-secondary text-muted-foreground hover:bg-gray-300 hover:-translate-y-0.5 border-border"
                    }`}
                    data-testid={`filter-${sub.slug}`}
                  >
                    <span>{sub.name}</span>
                    {subCount > 0 && (
                      <span className={`text-[10px] ${category === sub.slug ? 'text-white/70' : 'text-muted-foreground/70'}`}>
                        ({subCount})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-4 bg-secondary p-4 rounded-xl" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        {/* Mini Model Categories */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Mini Graphics
          </h3>
          <div className="space-y-1">
            {categories
              .filter(cat => cat.slug === 'mini-stripes')
              .map((rootCat) => {
                const mainCategories = categories
                  .filter(cat => cat.parent_id === rootCat.id)
                  .sort((a, b) => a.name.localeCompare(b.name));
                return mainCategories.map((cat) => (
                  <CategoryItem key={cat.id} cat={cat} />
                ));
              })}
          </div>
        </div>

        {/* Paint Protection */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            {t('paintProtection')}
          </h3>
          <div className="space-y-1">
            {categories
              .filter(cat => cat.slug === 'ppf' && !cat.parent_id)
              .map((ppfCat) => (
                <CategoryItem key={ppfCat.id} cat={ppfCat} />
              ))}
            {categories
              .filter(cat => cat.slug === 'ppf-tools' && !cat.parent_id)
              .map((toolsCat) => (
                <CategoryItem key={toolsCat.id} cat={toolsCat} />
              ))}
          </div>
        </div>

        {/* Vinyl */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            {t('vinyl') || 'Vinyl'}
          </h3>
          <div className="space-y-1">
            {categories
              .filter(cat => cat.slug === 'vinyl-tools' && !cat.parent_id)
              .map((vinylCat) => (
                <CategoryItem key={vinylCat.id} cat={vinylCat} />
              ))}
            {categories
              .filter(cat => cat.slug === 'wrap-vinyl' && !cat.parent_id)
              .map((wrapCat) => (
                <CategoryItem key={wrapCat.id} cat={wrapCat} />
              ))}
          </div>
        </div>

        {(category || searchQuery) && (
          <Button
            variant="outline"
            className="rounded-full text-sm font-medium px-5 bg-black text-white border-black hover:bg-gray-800"
            onClick={clearFilters}
            data-testid="clear-filters-btn"
          >
            <X className="h-3.5 w-3.5 mr-2" />
            Clear Filters
          </Button>
        )}
      </div>
    );
  };

  const getCategoryTitle = () => {
    if (category) {
      const cat = categories.find(c => c.slug === category);
      if (cat?.name) return cat.name;
      // Fallback: capitalize words properly if category not found yet
      return category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    return "All Products";
  };

  // Update document title when category changes
  useEffect(() => {
    const title = getCategoryTitle();
    document.title = title === "All Products" ? "Products | Mini Stripes UK" : `${title} | Mini Stripes UK`;
  }, [category, categories]);

  // Admin functions
  const handlePriceChange = (productId, newPrice) => {
    setPriceEdits(prev => ({ ...prev, [productId]: newPrice }));
  };

  const savePriceChanges = async () => {
    if (!token || Object.keys(priceEdits).length === 0) return;
    setSavingChanges(true);
    try {
      for (const [productId, newPrice] of Object.entries(priceEdits)) {
        await axios.patch(`${API}/products/${productId}`, 
          { price: parseFloat(newPrice) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setPriceEdits({});
      fetchProducts();
      setAdminEditMode(null);
    } catch (error) {
      console.error("Error saving prices:", error);
    } finally {
      setSavingChanges(false);
    }
  };

  const moveProduct = async (productId, direction) => {
    const currentIndex = products.findIndex(p => p.id === productId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= products.length) return;
    
    const newProducts = [...products];
    [newProducts[currentIndex], newProducts[newIndex]] = [newProducts[newIndex], newProducts[currentIndex]];
    setProducts(newProducts);
    
    // Save the new order to backend
    try {
      const productIds = newProducts.map(p => p.id);
      await axios.post(`${API}/products/reorder`, 
        { product_ids: productIds, category: category || 'all' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Error saving order:", error);
    }
  };

  // Quick edit functions
  const openQuickEdit = async (product) => {
    // Fetch fresh product data to ensure we have the latest images
    try {
      const response = await axios.get(`${API}/products/${product.id}`);
      const freshProduct = response.data;
      setEditingProduct(freshProduct);
      setEditFormData({
        name: freshProduct.name || '',
        price: freshProduct.price || 0,
        description: freshProduct.description || '',
        stock: freshProduct.stock || 0,
        sku: freshProduct.sku || '',
        visible: freshProduct.visible !== false,
        has_color_palette: freshProduct.has_color_palette !== false,
        material_id: freshProduct.material_id || ''
      });
      setEditModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch product:", error);
      // Fallback to passed product data
      setEditingProduct(product);
      setEditFormData({
        name: product.name || '',
        price: product.price || 0,
        description: product.description || '',
        stock: product.stock || 0,
        sku: product.sku || '',
        visible: product.visible !== false,
        has_color_palette: product.has_color_palette !== false,
        material_id: product.material_id || ''
      });
      setEditModalOpen(true);
    }
  };

  const saveQuickEdit = async () => {
    if (!editingProduct || !token) return;
    setSavingChanges(true);
    try {
      await axios.patch(`${API}/products/${editingProduct.id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditModalOpen(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      console.error("Error saving product:", error);
    } finally {
      setSavingChanges(false);
    }
  };

  // Save material price edits
  const saveMaterialPrices = async () => {
    if (!token) return;
    setSavingChanges(true);
    try {
      const updates = Object.entries(materialPriceEdits);
      for (const [materialId, newPrice] of updates) {
        await axios.patch(`${API}/materials/${materialId}`, 
          { price_modifier: parseFloat(newPrice) || 0 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      // Refresh materials list
      const res = await axios.get(`${API}/materials`);
      setMaterials(res.data);
      setEditingMaterialPrices(false);
      setMaterialPriceEdits({});
    } catch (error) {
      console.error("Error saving material prices:", error);
    } finally {
      setSavingChanges(false);
    }
  };

  // Category/Page settings functions
  const openPageSettings = () => {
    const currentCat = categories.find(c => c.slug === category);
    setCategorySettings({
      name: currentCat?.name || getCategoryTitle(),
      slug: category || '',
      seoTitle: currentCat?.seo_title || '',
      seoDescription: currentCat?.seo_description || '',
      seoKeywords: currentCat?.seo_keywords || '',
      displayOrder: currentCat?.display_order || 'default'
    });
    setPageSettingsOpen(true);
  };

  const saveCategorySettings = async () => {
    if (!category || !token) return;
    setSavingCategorySettings(true);
    try {
      await axios.patch(`${API}/categories/${category}`, {
        name: categorySettings.name,
        slug: categorySettings.slug,
        seo_title: categorySettings.seoTitle,
        seo_description: categorySettings.seoDescription,
        seo_keywords: categorySettings.seoKeywords,
        display_order: categorySettings.displayOrder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPageSettingsOpen(false);
      // Refresh categories with full list including subcategories
      const catRes = await axios.get(`${API}/categories/all`);
      setCategories(catRes.data);
      // If slug changed, redirect to new URL
      if (categorySettings.slug !== category) {
        window.location.href = `/products/${categorySettings.slug}`;
      }
    } catch (error) {
      console.error("Error saving category settings:", error);
    } finally {
      setSavingCategorySettings(false);
    }
  };

  // Admin Panel Component
  const AdminPanel = () => {
    if (!isAdmin) return null;
    
    return (
      <div className="mb-6">
        {/* Admin Toggle Button */}
        <div className="flex justify-end mb-2">
          <Button
            variant={adminPanelOpen ? "default" : "outline"}
            size="sm"
            onClick={() => setAdminPanelOpen(!adminPanelOpen)}
            className="rounded-full"
            data-testid="admin-panel-toggle"
          >
            <Settings className="h-4 w-4 mr-2" />
            Admin Tools
          </Button>
        </div>
        
        {/* Admin Panel Content */}
        {adminPanelOpen && (
          <div className="bg-gray-900 text-white rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider">Admin Controls</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setAdminPanelOpen(false); setAdminEditMode(null); }}
                className="text-white hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {/* Page Settings Button */}
              {category && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openPageSettings}
                  className="rounded-full border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black"
                  data-testid="admin-page-settings-btn"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Page Settings & SEO
                </Button>
              )}
              
              {/* Reorder Button */}
              <Button
                variant={adminEditMode === 'reorder' ? "secondary" : "outline"}
                size="sm"
                onClick={() => setAdminEditMode(adminEditMode === 'reorder' ? null : 'reorder')}
                className={`rounded-full ${adminEditMode === 'reorder' ? 'bg-white text-black' : 'border-white text-white hover:bg-white hover:text-black'}`}
                data-testid="admin-reorder-btn"
              >
                <GripVertical className="h-4 w-4 mr-2" />
                Reorder Listings
              </Button>
              
              {/* Price Edit Button */}
              <Button
                variant={adminEditMode === 'price' ? "secondary" : "outline"}
                size="sm"
                onClick={() => setAdminEditMode(adminEditMode === 'price' ? null : 'price')}
                className={`rounded-full ${adminEditMode === 'price' ? 'bg-white text-black' : 'border-white text-white hover:bg-white hover:text-black'}`}
                data-testid="admin-price-btn"
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Edit Prices
              </Button>
              
              {/* Quick Edit Button */}
              <Button
                variant={adminEditMode === 'edit' ? "secondary" : "outline"}
                size="sm"
                onClick={() => setAdminEditMode(adminEditMode === 'edit' ? null : 'edit')}
                className={`rounded-full ${adminEditMode === 'edit' ? 'bg-white text-black' : 'border-white text-white hover:bg-white hover:text-black'}`}
                data-testid="admin-edit-btn"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Quick Edit
              </Button>
              
              {/* Go to Full Admin */}
              <Link to="/admin">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full border-white text-white hover:bg-white hover:text-black"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Full Admin Panel
                </Button>
              </Link>
            </div>
            
            {/* Save Button for Price Changes */}
            {adminEditMode === 'price' && Object.keys(priceEdits).length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <Button
                  onClick={savePriceChanges}
                  disabled={savingChanges}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingChanges ? 'Saving...' : `Save ${Object.keys(priceEdits).length} Price Changes`}
                </Button>
              </div>
            )}
            
            {/* Mode Instructions */}
            {adminEditMode && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-400">
                  {adminEditMode === 'reorder' && '↑↓ Use arrows on products to reorder. Changes save automatically.'}
                  {adminEditMode === 'price' && '💰 Click on prices to edit. Save when done.'}
                  {adminEditMode === 'edit' && '✏️ Click edit icon on products to modify details.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      className="pt-24 pb-16 min-h-screen relative bg-white" 
      data-testid="products-page"
      style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
    >
      {/* Tiled wallpaper background - same as homepage */}
      <div 
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `url('/uploads/website/msuk-wallpaper.png')`,
          backgroundRepeat: 'repeat',
          backgroundSize: '320px',
          backgroundPosition: 'top left'
        }}
      />
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Admin Panel */}
        <AdminPanel />

        {/* Category Tabs - Vertical Grid Layout - Only show when no category selected */}
        {!category && (
        <div className="mb-6">
          {/* Loading/Error state */}
          {categories.length === 0 && (
            <div className="text-center py-12">
              {categoriesError ? (
                <>
                  <div className="text-red-400 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-medium mb-2">Unable to load categories</p>
                  <p className="text-gray-500 mb-4">The database may need to be initialized.</p>
                  <button
                    onClick={triggerDatabaseSeed}
                    disabled={seedingDatabase}
                    className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {seedingDatabase ? "Seeding Database..." : "Initialize Database"}
                  </button>
                </>
              ) : (
                <>
                  <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16 mx-auto animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <p className="text-gray-500">Loading categories...</p>
                </>
              )}
            </div>
          )}
          
          {/* Categories Grid */}
          {categories.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Get categories to display */}
            {(() => {
              console.log("Total categories:", categories.length);
              
              // Try to find mini-stripes root category
              const miniStripesRoot = categories.find(cat => 
                cat.slug === 'mini-stripes' || 
                cat.slug === 'all-mini' ||
                cat.name?.toLowerCase().includes('mini stripes') ||
                cat.name?.toLowerCase().includes('all mini')
              );
              
              console.log("Mini-stripes root found:", miniStripesRoot?.name || "NO");
              
              let subcats = [];
              if (miniStripesRoot) {
                subcats = categories
                  .filter(cat => cat.parent_id === miniStripesRoot.id && cat.name !== 'Mini 60 years')
                  .sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || a.name.localeCompare(b.name));
                console.log("Subcategories found:", subcats.length);
              }
              
              // Fallback: show ANY categories if no subcategories found
              let catsToShow = subcats;
              if (subcats.length === 0) {
                console.log("No subcategories, showing all available categories");
                // Show all categories sorted by name
                catsToShow = categories
                  .filter(cat => cat.name !== 'Mini 60 years')
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .slice(0, 16);
              }
              
              console.log("Categories to display:", catsToShow.length);
              
              if (catsToShow.length === 0) {
                return (
                  <div className="col-span-full text-center py-8">
                    <p className="text-gray-500 mb-4">No categories found.</p>
                    <p className="text-sm text-gray-400">Please seed the database by visiting /api/seed-database</p>
                  </div>
                );
              }
              
              return catsToShow.map((cat, index, arr) => (
                <div key={cat.id} className="relative group">
                  {categoryEditMode && isAdmin && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 flex gap-1 bg-white rounded-full shadow-lg p-1">
                      <button
                        onClick={() => moveCategoryOrder(cat.id, 'up')}
                        disabled={index === 0}
                        className="w-7 h-7 bg-zinc-700 text-white rounded-full flex items-center justify-center hover:bg-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move Earlier"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openCategoryEditModal(cat)}
                        className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600"
                        title="Edit Name & Image"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => moveCategoryOrder(cat.id, 'down')}
                        disabled={index === arr.length - 1}
                        className="w-7 h-7 bg-zinc-700 text-white rounded-full flex items-center justify-center hover:bg-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move Later"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <span className="w-7 h-7 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-xs font-bold" title="Position">
                        {index + 1}
                      </span>
                    </div>
                  )}
                  <Link
                    to={categoryEditMode ? '#' : `/products/${cat.slug}`}
                    onClick={(e) => categoryEditMode && e.preventDefault()}
                    className={`group w-full aspect-square flex items-center justify-center text-center text-base sm:text-lg md:text-xl lg:text-2xl font-light tracking-wide px-2 sm:px-3 md:px-4 transition-all whitespace-normal rounded-2xl relative overflow-hidden ${
                      category === cat.slug 
                        ? 'bg-gradient-to-b from-white via-gray-50 to-gray-100 text-black border border-gray-200' 
                        : 'bg-gradient-to-b from-zinc-600 via-zinc-700 to-zinc-800 text-white border border-zinc-500 hover:from-zinc-500 hover:via-zinc-600 hover:to-zinc-700'
                    }`}
                    style={{ 
                      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      boxShadow: category === cat.slug 
                        ? '0 4px 6px rgba(0,0,0,0.1), 0 10px 20px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.05)' 
                        : '0 4px 6px rgba(0,0,0,0.3), 0 10px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -1px 0 rgba(0,0,0,0.3)'
                    }}
                  >
                    {/* Mouse cursor icon - top right */}
                    <div className="absolute top-4 right-4 opacity-30 group-hover:opacity-100 transition-opacity">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M4 4l16 8-7 2-2 7-7-17z"/>
                      </svg>
                    </div>
                    {/* Split name and date range onto separate lines */}
                    {cat.name.includes('(') ? (
                      <span className="flex flex-col items-center leading-tight">
                        <span className="line-clamp-2">{cat.name.split('(')[0].trim()}</span>
                        <span className="text-xs sm:text-sm md:text-base mt-1">({cat.name.split('(')[1]}</span>
                      </span>
                    ) : (
                      <span className="line-clamp-3 leading-tight">{cat.name}</span>
                    )}
                  </Link>
                </div>
              ));
            })()}
            {/* PPF & Vinyl tabs */}
            {categories
              .filter(cat => ['ppf', 'ppf-tools', 'vinyl-tools', 'wrap-vinyl'].includes(cat.slug) && !cat.parent_id)
              .map(cat => (
                <div key={cat.id} className="relative group">
                  {categoryEditMode && isAdmin && (
                    <div className="absolute -top-2 -right-2 z-20 flex gap-1">
                      <button
                        onClick={() => openCategoryEditModal(cat)}
                        className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 shadow-lg"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <Link
                    to={categoryEditMode ? '#' : `/products/${cat.slug}`}
                    onClick={(e) => categoryEditMode && e.preventDefault()}
                    className={`group w-full aspect-square flex items-center justify-center text-center text-base sm:text-lg md:text-xl lg:text-2xl font-light tracking-wide px-2 sm:px-3 md:px-4 transition-all whitespace-normal rounded-2xl relative overflow-hidden ${
                      category === cat.slug 
                        ? 'bg-gradient-to-b from-white via-gray-50 to-gray-100 text-black border border-gray-200' 
                        : 'bg-gradient-to-b from-zinc-600 via-zinc-700 to-zinc-800 text-white border border-zinc-500 hover:from-zinc-500 hover:via-zinc-600 hover:to-zinc-700'
                    }`}
                    style={{ 
                      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      boxShadow: category === cat.slug 
                        ? '0 4px 6px rgba(0,0,0,0.1), 0 10px 20px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.05)' 
                        : '0 4px 6px rgba(0,0,0,0.3), 0 10px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -1px 0 rgba(0,0,0,0.3)'
                    }}
                  >
                    {/* Mouse cursor icon - top right */}
                    <div className="absolute top-4 right-4 opacity-30 group-hover:opacity-100 transition-opacity">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M4 4l16 8-7 2-2 7-7-17z"/>
                      </svg>
                    </div>
                    {/* Split name and date range onto separate lines */}
                    {cat.name.includes('(') ? (
                      <span className="flex flex-col items-center leading-tight">
                        <span className="line-clamp-2">{cat.name.split('(')[0].trim()}</span>
                        <span className="text-xs sm:text-sm md:text-base mt-1">({cat.name.split('(')[1]}</span>
                      </span>
                    ) : (
                      <span className="line-clamp-3 leading-tight">{cat.name}</span>
                    )}
                  </Link>
                </div>
              ))}
          </div>
          )}
          
          {/* Admin Edit Mode Toggle */}
          {isAdmin && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setCategoryEditMode(!categoryEditMode)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  categoryEditMode 
                    ? 'bg-red-500 text-white hover:bg-red-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {categoryEditMode ? 'Exit Edit Mode' : 'Edit Categories'}
              </button>
            </div>
          )}
        </div>
        )}

        {/* Only show toolbar and products when a category is selected */}
        {category && (
        <>
        {/* Category Edit Modal */}
        <Dialog open={categoryEditModal} onOpenChange={setCategoryEditModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Category Name</label>
                <Input
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Category name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Category Image</label>
                <div className="space-y-2">
                  {categoryFormData.image_url && (
                    <div className="relative w-full h-40 bg-gray-100 rounded-lg overflow-hidden">
                      <img 
                        src={categoryFormData.image_url} 
                        alt="Category" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => setCategoryFormData(prev => ({ ...prev, image_url: '' }))}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={categoryFormData.image_url}
                      onChange={(e) => setCategoryFormData(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="Image URL or upload below"
                      className="flex-1"
                    />
                  </div>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCategoryImageUpload}
                      className="hidden"
                      id="category-image-upload"
                    />
                    <label
                      htmlFor="category-image-upload"
                      className={`flex items-center justify-center gap-2 w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors ${uploadingCategoryImage ? 'opacity-50' : ''}`}
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingCategoryImage ? 'Uploading...' : 'Upload Image'}
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setCategoryEditModal(false)}>
                  Cancel
                </Button>
                <Button onClick={saveCategory} disabled={savingCategory}>
                  {savingCategory ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Toolbar Row - Minimal Text Links with Icons */}
        <div className="flex flex-wrap items-center gap-6 mb-8 pb-4 border-b border-gray-200">
          {/* Layout - Icon Toggle */}
          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 transition-colors ${viewMode === 'grid' ? 'text-black' : 'text-gray-400 hover:text-gray-600'}`}
              title="Grid view"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 transition-colors ${viewMode === 'list' ? 'text-black' : 'text-gray-400 hover:text-gray-600'}`}
              title="List view"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Right Side - Sort & Show */}
          <div className="flex items-center gap-6 ml-auto">
            {/* Sort By - Text Dropdown */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-auto border-0 shadow-none h-auto p-0 text-sm text-gray-700 hover:text-black focus:ring-0" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Sort By</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price-desc">Price: High to Low</SelectItem>
                <SelectItem value="name-asc">Name: A-Z</SelectItem>
                <SelectItem value="name-desc">Name: Z-A</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Show - Text Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Show:</span>
              <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
                <SelectTrigger className="w-auto border-0 shadow-none h-auto p-0 text-sm text-gray-700 hover:text-black focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12</SelectItem>
                  <SelectItem value="24">24</SelectItem>
                  <SelectItem value="48">48</SelectItem>
                  <SelectItem value="96">96</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="w-full">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="space-y-4">
                    <div className="aspect-[4/5] bg-secondary animate-pulse" />
                    <div className="h-4 bg-secondary animate-pulse w-3/4" />
                    <div className="h-4 bg-secondary animate-pulse w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="py-8">
                {/* Check if current category has subcategories */}
                {(() => {
                  const currentCat = categories.find(c => c.slug === category);
                  const subcats = currentCat ? categories.filter(c => c.parent_id === currentCat.id) : [];
                  
                  if (subcats.length > 0) {
                    return (
                      <div data-testid="subcategories-grid">
                        <p className="text-muted-foreground mb-6 text-center">
                          Browse our {currentCat?.name} categories:
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {subcats.map(sub => (
                            <Link 
                              key={sub.id}
                              to={`/products/${sub.slug}`}
                              className={`group w-full aspect-square flex items-center justify-center text-center text-base sm:text-lg md:text-xl font-light tracking-wide px-2 sm:px-3 md:px-4 transition-all whitespace-normal rounded-2xl relative overflow-hidden bg-gradient-to-b from-zinc-600 via-zinc-700 to-zinc-800 text-white border border-zinc-500 hover:from-zinc-500 hover:via-zinc-600 hover:to-zinc-700`}
                              style={{ 
                                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.3), 0 10px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -1px 0 rgba(0,0,0,0.3)'
                              }}
                              data-testid={`subcat-link-${sub.slug}`}
                            >
                              {/* Mouse cursor icon - top right */}
                              <div className="absolute top-4 right-4 opacity-30 group-hover:opacity-100 transition-opacity">
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M4 4l16 8-7 2-2 7-7-17z"/>
                                </svg>
                              </div>
                              {/* Split name and date range onto separate lines */}
                              {sub.name.includes('(') ? (
                                <span className="flex flex-col items-center leading-tight">
                                  <span className="line-clamp-2">{sub.name.split('(')[0].trim()}</span>
                                  <span className="text-xs sm:text-sm md:text-base mt-1">({sub.name.split('(')[1]}</span>
                                </span>
                              ) : (
                                <span className="line-clamp-3 leading-tight">{sub.name}</span>
                              )}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">No products found</p>
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={clearFilters}
                      >
                        Clear Filters
                      </Button>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <>
                {/* Grid View */}
                {viewMode === 'grid' && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
                    {products.map((product, index) => (
                      <div key={product.id} className="relative group">
                        {/* Admin Reorder Arrows - Always visible for admins */}
                        {isAdmin && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 flex gap-1 bg-white rounded-full shadow-lg p-1 opacity-60 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveProduct(product.id, 'up'); }}
                              disabled={index === 0}
                              className="w-7 h-7 bg-zinc-700 text-white rounded-full flex items-center justify-center hover:bg-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move Earlier"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="w-7 h-7 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-xs font-bold" title="Position">
                              {index + 1}
                            </span>
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveProduct(product.id, 'down'); }}
                              disabled={index === products.length - 1}
                              className="w-7 h-7 bg-zinc-700 text-white rounded-full flex items-center justify-center hover:bg-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move Later"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        
                        {/* Admin Price Edit Overlay */}
                        {isAdmin && adminEditMode === 'price' && (
                          <div className="absolute bottom-20 left-0 right-0 z-20 px-2">
                            <div className="bg-black rounded-lg p-2 flex items-center gap-2">
                              <span className="text-white text-sm">£</span>
                              <Input
                                type="number"
                                step="0.01"
                                defaultValue={product.price}
                                onChange={(e) => handlePriceChange(product.id, e.target.value)}
                                className="h-8 text-sm bg-white"
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Admin Quick Edit Button - Always visible for admins */}
                        {isAdmin && (
                          <Button
                            variant="secondary"
                            size="icon"
                            className="absolute top-2 right-2 z-20 h-10 w-10 rounded-full bg-blue-600 text-white hover:bg-blue-700 shadow-lg"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (adminEditMode === 'edit') {
                                openQuickEdit(product);
                              } else {
                                window.location.href = `/product/${product.id}?edit=true`;
                              }
                            }}
                            title={adminEditMode === 'edit' ? "Quick Edit" : "Edit Listing"}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        
                        <ProductCard product={product} />
                      </div>
                    ))}
                  </div>
                )}

                {/* List View */}
                {viewMode === 'list' && (
                  <div className="space-y-4">
                    {products.map((product, index) => (
                      <div 
                        key={product.id} 
                        className="flex gap-6 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                      >
                        {/* Product Image */}
                        <Link 
                          to={`/product/${product.id}`}
                          className="shrink-0 w-40 h-40 bg-gray-100 rounded-lg overflow-hidden"
                        >
                          {product.image_url ? (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-full h-full object-cover hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <ImageIcon className="h-12 w-12" />
                            </div>
                          )}
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Numbered Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12" data-testid="pagination">
                    {/* Previous Button */}
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full h-10 w-10"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    {/* Page Numbers */}
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                      
                      // Adjust start if we're near the end
                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }
                      
                      // First page + ellipsis
                      if (startPage > 1) {
                        pages.push(
                          <Button
                            key={1}
                            variant="outline"
                            className={`rounded-full h-10 w-10 ${currentPage === 1 ? "bg-primary text-primary-foreground" : ""}`}
                            onClick={() => handlePageChange(1)}
                            data-testid="pagination-1"
                          >
                            1
                          </Button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span key="ellipsis-start" className="px-2 text-muted-foreground">...</span>
                          );
                        }
                      }
                      
                      // Visible page numbers
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Button
                            key={i}
                            variant="outline"
                            className={`rounded-full h-10 w-10 ${currentPage === i ? "bg-primary text-primary-foreground hover:bg-primary" : ""}`}
                            onClick={() => handlePageChange(i)}
                            data-testid={`pagination-${i}`}
                          >
                            {i}
                          </Button>
                        );
                      }
                      
                      // Ellipsis + last page
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span key="ellipsis-end" className="px-2 text-muted-foreground">...</span>
                          );
                        }
                        pages.push(
                          <Button
                            key={totalPages}
                            variant="outline"
                            className={`rounded-full h-10 w-10 ${currentPage === totalPages ? "bg-primary text-primary-foreground" : ""}`}
                            onClick={() => handlePageChange(totalPages)}
                            data-testid={`pagination-${totalPages}`}
                          >
                            {totalPages}
                          </Button>
                        );
                      }
                      
                      return pages;
                    })()}
                    
                    {/* Next Button */}
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full h-10 w-10"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      data-testid="pagination-next"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
        )}

        {/* Adverts Section - Bottom of Page */}
        {(allAdverts.filter(ad => !ad.description?.toLowerCase().includes('none mini') && !ad.description?.toLowerCase().includes('graphics store')).length > 0) && (
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {allAdverts.filter(ad => !ad.description?.toLowerCase().includes('none mini') && !ad.description?.toLowerCase().includes('graphics store')).map((advert) => (
                <div key={advert.id} className="p-4 border-2 border-gray-300 rounded-2xl bg-gray-50 shadow-sm">
                  {advert.image_url && (
                    <div className="relative group">
                      {advert.link_url ? (
                        <a 
                          href={advert.link_url.startsWith('http') ? advert.link_url : `https://${advert.link_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-3/4 mx-auto rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02]"
                        >
                          <img 
                            src={advert.image_url} 
                            alt={advert.title || "Advert"} 
                            className="w-full h-auto object-contain transition-transform duration-300 group-hover:brightness-105"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </a>
                      ) : (
                        <div className="w-3/4 mx-auto rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md hover:scale-[1.02]">
                          <img 
                            src={advert.image_url} 
                            alt={advert.title || "Advert"} 
                            className="w-full h-auto object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {advert.description && (
                    <p className="text-sm font-semibold text-gray-800 mt-3 text-center">{advert.description}</p>
                  )}
                </div>
              ))}
              
              {/* Auto Bath Detailing Advert */}
              <div className="p-4 border-2 border-gray-300 rounded-2xl bg-gray-50 shadow-sm">
                <div className="relative group">
                  <a 
                    href="https://autobathdetailing.co.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-3/4 mx-auto rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02]"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_e19874b7-6efa-4f51-b23d-f8dbaa173ae0/artifacts/ulnkwfug_ad.jpg" 
                      alt="Auto Bath Detailing Edinburgh" 
                      className="w-full h-auto object-contain transition-transform duration-300 group-hover:brightness-105"
                    />
                  </a>
                </div>
                <a 
                  href="https://autobathdetailing.co.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:underline"
                >
                  <p className="text-sm font-semibold text-gray-800 mt-3 text-center">
                    Associated with Auto Bath Detailing Edinburgh<br />
                    for all your PPF and High end Detailing
                  </p>
                </a>
              </div>
            </div>
          </div>
        )}
        
        {/* Brand Partners Banner - Black Background */}
        <div className="mt-16 border-t border-gray-800 pt-8 pb-8 bg-black flex justify-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_9a897fba-e8dc-4e39-998a-a577303a6f73/artifacts/of8rsh7b_3.png" 
            alt="Brand Partners - Graphtec, Roland, Hexis, 3M, KPMF, Avery Dennison, Metamark, Oracal, Olfa, Easymount" 
            className="h-auto min-h-[40px] brightness-0 invert max-w-[85%]"
            style={{ objectFit: 'contain' }}
            loading="lazy"
          />
        </div>
      </div>

      {/* Quick Edit Modal - v2 with multi-image support */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Quick Edit Product
            </DialogTitle>
          </DialogHeader>
          
          {editingProduct && (
            <div className="space-y-4 py-4">
              {/* Product Info Header */}
              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                {editingProduct.image_url ? (
                  <img 
                    src={editingProduct.image_url} 
                    alt={editingProduct.name}
                    className="h-16 w-16 object-cover rounded"
                  />
                ) : (
                  <div className="h-16 w-16 bg-gray-200 rounded flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-sm text-gray-500">ID: {editingProduct.id}</p>
                  <p className="text-sm text-gray-500">SKU: {editingProduct.sku || 'N/A'}</p>
                </div>
              </div>

              {/* Product Images Section */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                {/* Create display images array that includes image_url if images array is empty */}
                {(() => {
                  const displayImages = (editingProduct.images && editingProduct.images.length > 0) 
                    ? editingProduct.images 
                    : (editingProduct.image_url ? [editingProduct.image_url] : []);
                  
                  return (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          Product Images ({displayImages.length})
                        </span>
                        <span className="text-xs text-orange-600 font-medium">
                          #1 = Main listing image
                        </span>
                      </div>
                      
                      {displayImages.length > 0 ? (
                        <>
                          <div className="grid grid-cols-5 gap-2">
                            {displayImages.map((img, idx) => (
                        <div 
                          key={img + idx} 
                          className="relative group"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', idx.toString());
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                          onDrop={async (e) => {
                            e.preventDefault();
                            e.currentTarget.style.transform = 'scale(1)';
                            const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                            const toIdx = idx;
                            if (fromIdx === toIdx) return;
                            
                            const images = [...(editingProduct.images || [])];
                            const [movedItem] = images.splice(fromIdx, 1);
                            images.splice(toIdx, 0, movedItem);
                            
                            try {
                              await axios.put(
                                `${API}/products/${editingProduct.id}/images/reorder`,
                                images,
                                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
                              );
                              setEditingProduct({...editingProduct, images, image_url: images[0] || ""});
                            } catch (error) {
                              console.error("Failed to reorder images");
                            }
                          }}
                        >
                          {/* Order Number */}
                          <div className={`absolute -top-1 -left-1 z-10 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shadow ${idx === 0 ? 'bg-orange-500 text-white' : 'bg-white text-gray-700 border border-gray-300'}`}>
                            {idx + 1}
                          </div>
                          
                          {/* Image */}
                          <div className={`aspect-square rounded-lg overflow-hidden cursor-grab active:cursor-grabbing ${idx === 0 ? 'ring-2 ring-orange-500' : 'border border-gray-200'}`}>
                            <img src={img} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
                          </div>
                          
                          {/* Delete button - always visible */}
                          <button
                            type="button"
                            draggable={false}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!window.confirm("Delete this image?")) return;
                              
                              const authToken = localStorage.getItem('auth_token');
                              if (!authToken) {
                                alert("Please login first");
                                return;
                              }
                              
                              try {
                                const response = await axios.delete(
                                  `${API}/products/${editingProduct.id}/images/${idx}`,
                                  { headers: { Authorization: `Bearer ${authToken}` } }
                                );
                                const updated = response.data.images || [];
                                setEditingProduct(prev => ({...prev, images: updated, image_url: updated[0] || ""}));
                                alert("Image deleted successfully");
                              } catch (error) {
                                console.error("Delete error:", error);
                                alert("Failed to delete: " + (error.response?.data?.detail || error.message));
                              }
                            }}
                            className="absolute -top-1 -right-1 z-20 p-1.5 bg-red-500 rounded-full hover:bg-red-600 shadow-lg border-2 border-white"
                            title="Delete image"
                          >
                            <X className="h-3 w-3 text-white" />
                          </button>
                        </div>
                      ))}
                      
                      {/* Add Image Button */}
                      <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-400 cursor-pointer flex flex-col items-center justify-center text-gray-400 hover:text-orange-500 transition-colors bg-white">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file || !editingProduct) return;
                            
                            const formDataUpload = new FormData();
                            formDataUpload.append('file', file);
                            
                            try {
                              const response = await axios.post(
                                `${API}/products/${editingProduct.id}/add-image`,
                                formDataUpload,
                                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
                              );
                              setEditingProduct({...editingProduct, images: response.data.images, image_url: response.data.images[0] || editingProduct.image_url});
                            } catch (error) {
                              console.error("Failed to add image");
                            }
                          }}
                        />
                        <Plus className="h-5 w-5" />
                        <span className="text-[9px] mt-0.5">Add</span>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Drag to reorder • Hover to delete • #1 is main image
                    </p>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <ImageIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500 mb-2">No images yet</p>
                    <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg cursor-pointer transition-colors text-sm">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file || !editingProduct) return;
                          
                          const formDataUpload = new FormData();
                          formDataUpload.append('file', file);
                          
                          try {
                            const response = await axios.post(
                              `${API}/products/${editingProduct.id}/add-image`,
                              formDataUpload,
                              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
                            );
                            setEditingProduct({...editingProduct, images: response.data.images, image_url: response.data.image_url});
                          } catch (error) {
                            console.error("Failed to add image");
                          }
                        }}
                      />
                      <Upload className="h-4 w-4" />
                      Upload First Image
                    </label>
                  </div>
                )}
                    </>
                  );
                })()}
              </div>

              {/* Name */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Product Name</label>
                <Input
                  value={editFormData.name}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Product name"
                />
              </div>

              {/* Price and Stock */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Price (£)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editFormData.price}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Stock</label>
                  <Input
                    type="number"
                    value={editFormData.stock}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* SKU */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">SKU</label>
                <Input
                  value={editFormData.sku}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, sku: e.target.value }))}
                  placeholder="Product SKU"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                <Textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Product description"
                  rows={3}
                />
              </div>

              {/* Visibility Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  {editFormData.visible ? (
                    <Eye className="h-4 w-4 text-green-600" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="text-sm font-medium">Product Visibility</span>
                </div>
                <Button
                  variant={editFormData.visible ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditFormData(prev => ({ ...prev, visible: !prev.visible }))}
                >
                  {editFormData.visible ? 'Visible' : 'Hidden'}
                </Button>
              </div>

              {/* Show Colour Palette Toggle */}
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="quick-edit-has-color-palette"
                  checked={editFormData.has_color_palette}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, has_color_palette: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <label htmlFor="quick-edit-has-color-palette" className="flex-1">
                  <span className="block text-sm font-medium text-gray-700">Show Colour Palette</span>
                  <span className="block text-xs text-gray-500">Enable HEXIS color selection</span>
                </label>
                <Palette className="h-5 w-5 text-gray-400" />
              </div>

              {/* Show Materials Toggle & Dropdown */}
              <div className="p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="quick-edit-show-material"
                    checked={!!editFormData.material_id}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setEditFormData(prev => ({ ...prev, material_id: '' }));
                      }
                    }}
                    className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <label htmlFor="quick-edit-show-material" className="flex-1">
                    <span className="block text-sm font-medium text-gray-700">Show Materials</span>
                    <span className="block text-xs text-gray-500">Display material type on product</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (editingMaterialPrices) {
                          setEditingMaterialPrices(false);
                          setMaterialPriceEdits({});
                        } else {
                          setEditingMaterialPrices(true);
                          const priceMap = {};
                          materials.forEach(m => { priceMap[m.id] = m.price_modifier; });
                          setMaterialPriceEdits(priceMap);
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      {editingMaterialPrices ? 'Cancel' : 'Edit Prices'}
                    </button>
                    <Ruler className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                
                {/* Material Selection or Price Editing */}
                <div className="mt-3 pl-8">
                  {!editingMaterialPrices ? (
                    <select
                      value={editFormData.material_id || ""}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, material_id: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Select Material...</option>
                      {materials.map((mat) => (
                        <option key={mat.id} value={mat.id}>
                          {mat.name} {mat.price_modifier !== 0 && `(${mat.price_modifier >= 0 ? '+' : ''}£${mat.price_modifier.toFixed(2)})`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-600 font-medium">Edit Material Prices:</p>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {materials.map((mat) => (
                          <div key={mat.id} className="flex items-center gap-2 bg-gray-50 p-2 rounded border border-gray-200">
                            <span className="flex-1 text-xs text-gray-700 truncate" title={mat.name}>
                              {mat.name}
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500">£</span>
                              <input
                                type="number"
                                step="0.01"
                                value={materialPriceEdits[mat.id] ?? mat.price_modifier}
                                onChange={(e) => setMaterialPriceEdits(prev => ({
                                  ...prev,
                                  [mat.id]: e.target.value
                                }))}
                                className="w-20 px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-orange-500"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        onClick={saveMaterialPrices}
                        disabled={savingChanges}
                        className="w-full mt-2 bg-orange-600 hover:bg-orange-700 text-white text-xs"
                      >
                        {savingChanges ? 'Saving...' : 'Save Material Prices'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setEditModalOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveQuickEdit}
                  disabled={savingChanges}
                  className="flex-1 bg-black hover:bg-gray-800"
                >
                  {savingChanges ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Page Settings Modal */}
      <Dialog open={pageSettingsOpen} onOpenChange={setPageSettingsOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Page Settings & SEO
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Page Name Section */}
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Pencil className="h-4 w-4" />
                Page Name
              </h4>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Display Name</label>
                <Input
                  value={categorySettings.name}
                  onChange={(e) => setCategorySettings(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Category display name"
                />
                <p className="text-xs text-gray-500 mt-1">This name appears as the page title</p>
              </div>
            </div>

            {/* URL/Slug Section */}
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Search className="h-4 w-4" />
                Search Engine Friendly URL
              </h4>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">URL Slug</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">/products/</span>
                  <Input
                    value={categorySettings.slug}
                    onChange={(e) => setCategorySettings(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') }))}
                    placeholder="category-slug"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Use lowercase letters, numbers, and hyphens only</p>
              </div>
            </div>

            {/* SEO Section */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Search className="h-4 w-4 text-blue-600" />
                SEO Settings
              </h4>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">SEO Title</label>
                <Input
                  value={categorySettings.seoTitle}
                  onChange={(e) => setCategorySettings(prev => ({ ...prev, seoTitle: e.target.value }))}
                  placeholder="SEO page title (60 chars recommended)"
                  maxLength={70}
                />
                <p className="text-xs text-gray-500 mt-1">{categorySettings.seoTitle?.length || 0}/70 characters</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">SEO Description</label>
                <Textarea
                  value={categorySettings.seoDescription}
                  onChange={(e) => setCategorySettings(prev => ({ ...prev, seoDescription: e.target.value }))}
                  placeholder="Meta description for search engines (160 chars recommended)"
                  maxLength={170}
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">{categorySettings.seoDescription?.length || 0}/170 characters</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">SEO Keywords</label>
                <Input
                  value={categorySettings.seoKeywords}
                  onChange={(e) => setCategorySettings(prev => ({ ...prev, seoKeywords: e.target.value }))}
                  placeholder="keyword1, keyword2, keyword3"
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated keywords</p>
              </div>
            </div>

            {/* Display Order Section */}
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <GripVertical className="h-4 w-4" />
                Products Display Order
              </h4>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Default Sort Order</label>
                <Select
                  value={categorySettings.displayOrder}
                  onValueChange={(value) => setCategorySettings(prev => ({ ...prev, displayOrder: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select display order" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default (Manual Order)</SelectItem>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    <SelectItem value="name-az">Name: A to Z</SelectItem>
                    <SelectItem value="name-za">Name: Z to A</SelectItem>
                    <SelectItem value="popular">Most Popular</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setPageSettingsOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveCategorySettings}
                disabled={savingCategorySettings}
                className="flex-1 bg-black hover:bg-gray-800"
              >
                {savingCategorySettings ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
